import { Link } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail, Settings, Clock, CheckCircle2, Sun, Moon } from 'lucide-react';
import Logo from '@components/ui/Logo';
import { useTheme } from '@context/ThemeContext';

const Step = ({ number, icon: Icon, title, children }) => (
  <div className="flex gap-4">
    <div className="flex flex-col items-center">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-red-500 to-rose-600 text-white text-sm font-bold shadow-md shadow-red-500/20">
        {number}
      </div>
      <div className="flex-1 w-px bg-surface-200 dark:bg-surface-700 mt-2" />
    </div>
    <div className="pb-8">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={16} className="text-surface-500" />
        <h3 className="text-sm font-bold text-surface-900 dark:text-white">{title}</h3>
      </div>
      <div className="text-sm leading-relaxed text-surface-600 dark:text-surface-400">
        {children}
      </div>
    </div>
  </div>
);

const DeleteAccountPage = () => {
  const { dark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
      <header className="sticky top-0 z-40 glass-header">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <Link to="/" className="flex items-center gap-2 text-surface-500 hover:text-surface-800 dark:hover:text-white transition-colors">
            <ArrowLeft size={18} />
            <Logo size="sm" />
          </Link>
          <button onClick={toggleTheme} className="theme-toggle" aria-label="Toggle dark mode">
            {dark ? <Sun size={18} strokeWidth={2} /> : <Moon size={18} strokeWidth={2} />}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-20">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 dark:bg-red-500/10">
            <Trash2 size={20} className="text-red-500" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-surface-900 dark:text-white">
            Delete Your Account
          </h1>
        </div>
        <p className="text-xs text-surface-400 dark:text-surface-500 mb-10">
          Last updated: March 12, 2026
        </p>

        <div className="rounded-2xl border border-surface-200/60 bg-white p-6 sm:p-8 shadow-sm dark:border-surface-800/60 dark:bg-surface-900">
          <section className="mb-8">
            <p className="text-sm leading-relaxed text-surface-600 dark:text-surface-400">
              We respect your right to control your personal data. If you wish to delete your
              Yaarvi account and all associated personal information, you can do so by following
              the steps below. Once your data is deleted, this action <strong className="text-surface-800 dark:text-surface-200">cannot be undone</strong>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-lg font-bold text-surface-900 dark:text-white mb-6">
              How to Request Account Deletion
            </h2>

            <div>
              <Step number={1} icon={Settings} title="Option A: Delete from your profile">
                <p>
                  Log in to your Yaarvi account, go to your <strong className="text-surface-800 dark:text-surface-200">Profile</strong> page,
                  and look for the <strong className="text-surface-800 dark:text-surface-200">&ldquo;Delete Account&rdquo;</strong> option
                  in account settings. Follow the prompts to confirm deletion.
                </p>
              </Step>

              <Step number={2} icon={Mail} title="Option B: Email us directly">
                <p>
                  If you prefer, you can send an email to{' '}
                  <a href="mailto:support@yaarvi.com" className="text-brand-500 hover:text-brand-600 font-semibold underline underline-offset-2">
                    support@yaarvi.com
                  </a>{' '}
                  with the subject line <strong className="text-surface-800 dark:text-surface-200">&ldquo;Account Deletion Request&rdquo;</strong>.
                  Please include the email address associated with your account so we can verify your identity.
                </p>
              </Step>

              <Step number={3} icon={Clock} title="Processing time">
                <p>
                  We will process your deletion request within <strong className="text-surface-800 dark:text-surface-200">30 days</strong> of
                  receiving it. You will receive a confirmation email once your data has been removed.
                </p>
              </Step>

              <Step number={4} icon={CheckCircle2} title="What gets deleted">
                <p>Upon deletion, the following data will be permanently removed from our systems:</p>
                <ul className="list-disc pl-5 space-y-1.5 mt-2">
                  <li>Your name, email address, and profile picture.</li>
                  <li>Your vibe history (vibes created and joined).</li>
                  <li>Your messages and chat history.</li>
                  <li>Your location data and preferences.</li>
                  <li>Any other personal data associated with your account.</li>
                </ul>
              </Step>
            </div>
          </section>

          <section className="mb-8 rounded-xl bg-amber-50 border border-amber-200/60 p-5 dark:bg-amber-500/5 dark:border-amber-500/20">
            <h3 className="text-sm font-bold text-amber-800 dark:text-amber-400 mb-2">
              Important Notes
            </h3>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-amber-700 dark:text-amber-400/80">
              <li>Account deletion is <strong>permanent and irreversible</strong>. You will not be able to recover your account or data after deletion.</li>
              <li>If you have any active vibes, they will be removed from the platform.</li>
              <li>We may retain certain anonymized, aggregated data that cannot be used to identify you, for analytics and service improvement purposes.</li>
              <li>Some data may be retained temporarily for legal compliance, fraud prevention, or to resolve disputes, as permitted by applicable law.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-surface-900 dark:text-white mb-3">
              Questions?
            </h2>
            <p className="text-sm leading-relaxed text-surface-600 dark:text-surface-400">
              If you have any questions about the data deletion process or need assistance, please
              contact us at{' '}
              <a href="mailto:support@yaarvi.com" className="text-brand-500 hover:text-brand-600 font-semibold underline underline-offset-2">
                support@yaarvi.com
              </a>.
              We&apos;re here to help.
            </p>
          </section>
        </div>

        <div className="mt-8 flex flex-wrap gap-4 text-xs text-surface-400 dark:text-surface-500">
          <Link to="/privacy" className="hover:text-brand-500 transition-colors underline underline-offset-2">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-brand-500 transition-colors underline underline-offset-2">Terms of Service</Link>
        </div>
      </main>
    </div>
  );
};

export default DeleteAccountPage;
